import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AirSearchResponseManager from 'obt-common/services/AirSearchResponseManager';
import { IRefundChangePolicy, RestrictionTypes, IPolicyInfo } from 'obt-common/types/flight';
import { RateOptionTicketType } from 'obt-common/types/api/v1/obt/air/air_search_response';
import { getItineraryRestrictions } from 'obt-common/utils/Flights/getPolicyInfo';
import { Container as PolicyTabContainer } from '../PolicyTabs/style';
import PolicyTabs from '../PolicyTabs/index';
import SeparateTicketBookingContent from '../../../flights/shared/SeparateTicketBookingContent';
import { Container, ContentWrapper, ContentHeader, RulesList, restriction_item } from './index.styles';
import PolicyViolations from './PolicyViolations';

interface IFlight {
  departureAirport: string;
  arrivalAirport: string;
}

interface IProps {
  policyInfos: (IPolicyInfo | undefined)[];
  ticketType: RateOptionTicketType | undefined;
  flights?: IFlight[];
  isOutOfPolicy: boolean[];
  isNotAllowedBooking?: boolean;
  refundChangePolicy?: IRefundChangePolicy;
  showOnlyOutofPolicy: boolean;
}

const PolicyContent = ({
  policyInfos,
  ticketType,
  flights,
  isOutOfPolicy,
  isNotAllowedBooking,
  refundChangePolicy,
  showOnlyOutofPolicy,
}: IProps): JSX.Element => {
  const { t: tt } = useTranslation('WEB');

  const [currentFlightIndex, setCurrentFlightIndex] = useState(0);
  const outOfPolicyRuleInfos = AirSearchResponseManager.getFareOutOfPolicyRuleResultInfos(
    policyInfos[currentFlightIndex],
  );
  const notAllowedRuleInfos = AirSearchResponseManager.getFareNotAllowedRuleResultInfos(
    policyInfos[currentFlightIndex],
  );

  const handleChangeFlightIndex = (flightIndex: number): void => {
    setCurrentFlightIndex(flightIndex);
  };

  const isMultiTicket = ticketType === RateOptionTicketType.MULTI;
  const isItineraryOutOfPolicy = isOutOfPolicy.some((data) => !!data);
  const isNonRefundable = refundChangePolicy ? !refundChangePolicy.refundable : false;
  const isNonChangeable = refundChangePolicy ? !refundChangePolicy.changeable : false;

  const { restrictions } = getItineraryRestrictions({
    isOutOfPolicy: isItineraryOutOfPolicy,
    isMultiTicket,
    isNonRefundable,
    isNonChangeable,
    isNotAllowedBooking: isNotAllowedBooking || false,
  });

  const onlyOutOfPolicyRestriciton = restrictions.filter(
    (restriction) => restriction === RestrictionTypes.OUT_OF_POLICY,
  );

  const restrictionsToBeShown = showOnlyOutofPolicy ? onlyOutOfPolicyRestriciton : restrictions;
  return (
    <Container data-testid="policy-content">
      {restrictionsToBeShown.map((restriction) => (
        <Fragment key={restriction}>
          {restriction === RestrictionTypes.OUT_OF_POLICY && (
            <div css={restriction_item}>
              {!showOnlyOutofPolicy && <ContentHeader>{tt('Out of policy')}</ContentHeader>}
              {flights && flights.length === 1 && <PolicyTabContainer />}
              <PolicyTabs
                flights={flights}
                flightIndex={currentFlightIndex}
                onChangeFlightIndex={handleChangeFlightIndex}
              />
              <PolicyViolations ruleInfos={outOfPolicyRuleInfos} />
            </div>
          )}

          {restriction === RestrictionTypes.NOT_ALLOWED && (
            <div css={restriction_item}>
              <ContentHeader>{tt('Not allowed')}</ContentHeader>
              {flights && flights.length === 1 && <PolicyTabContainer />}
              <PolicyTabs
                flights={flights}
                flightIndex={currentFlightIndex}
                onChangeFlightIndex={handleChangeFlightIndex}
              />
              <PolicyViolations ruleInfos={notAllowedRuleInfos} />
            </div>
          )}

          {restriction === RestrictionTypes.IS_MULTI_TICKET && (
            <div css={restriction_item}>
              <ContentHeader>{tt('Separate bookings')}</ContentHeader>
              <SeparateTicketBookingContent isLabelled={false} />
            </div>
          )}

          {restriction === RestrictionTypes.NON_REFUNDABLE && (
            <div css={restriction_item}>
              <ContentHeader>{tt('Non-refundable booking')}</ContentHeader>
              <ContentWrapper aria-label={tt('Non-refundable booking')}>
                <RulesList>{tt('The fare is non-refundable for the entire booking.')}</RulesList>
              </ContentWrapper>
            </div>
          )}

          {restriction === RestrictionTypes.NON_CHANGEABLE && (
            <div css={restriction_item}>
              <ContentHeader>{tt('Non-changeable booking')}</ContentHeader>
              <ContentWrapper>
                <RulesList aria-label={tt('Non-changeable booking')}>
                  {tt('Flights in this booking cannot be changed once booked.')}
                </RulesList>
              </ContentWrapper>
            </div>
          )}
        </Fragment>
      ))}
    </Container>
  );
};

export default PolicyContent;
